import { CSSProperties } from "react";
import I18n from "common/i18n";
import { btnDelay, usePlaySound } from "common/utils";
import { PlanBadge, getPlanColor } from "common/elements";
import { AskParentAction } from "features/subscription/components";
import {
  PLAN_COMPARISON_SECTIONS,
  PlanComparisonSubItem,
  formatPlanLimit,
} from "features/user/constants";
import {
  PlanType,
  PlanCategory,
  BillingPeriod,
  PlanLimits,
  PLAN_LIMITS,
} from "features/subscription";
import { UserPlanBillingToggle } from "./UserPlanBillingToggle";

const formatItemValue = (
  limits: PlanLimits,
  item: { key: PlanComparisonSubItem["key"]; format?: "bytes"; unitKey?: string },
): string => {
  const quota = limits[item.key];
  if (quota.unlimited) return I18n.t("MSG_USER_PLAN_UNLIMITED");
  const value = formatPlanLimit(quota.limit, quota.unlimited, item.format);
  if (item.format === "bytes") return value;
  const unit = item.unitKey ? I18n.t(item.unitKey) : "";
  return `${value}${unit}`;
};

type Props = {
  plan: PlanType;
  isCurrent: boolean;
  priceString?: string;
  category?: PlanCategory;
  onUpgrade?: (category: PlanCategory) => void;
  billingPeriod?: BillingPeriod;
  onChangeBillingPeriod?: (period: BillingPeriod) => void;
};

export const UserPlanComparisonCard = ({
  plan,
  isCurrent,
  priceString,
  category,
  onUpgrade,
  billingPeriod,
  onChangeBillingPeriod,
}: Props) => {
  const play = usePlaySound();
  const accent = getPlanColor(plan);
  const limits = PLAN_LIMITS[plan];

  const handleUpgradeClick = () => {
    if (!category || !onUpgrade) return;
    play();
    btnDelay(() => onUpgrade(category));
  };

  const isFreeOrTrial = plan === PlanType.FREE || plan === PlanType.TRIAL;
  const showBillingToggle =
    !isFreeOrTrial && !!billingPeriod && !!onChangeBillingPeriod && !isCurrent;
  const showAnnualDealBadge = showBillingToggle && billingPeriod === "annual";

  return (
    <div
      className="flex-col-el flex-1 h-full rounded-2xl border-2 border-solid border-[color:var(--plan-accent)] bg-white overflow-hidden shadow-[0_4px_12px_rgba(63,74,97,0.08)]"
      style={{ "--plan-accent": accent } as CSSProperties}
    >
      <div className="flex-col-el items-center justify-center py-3 px-3 shrink-0 bg-[color:var(--plan-accent)]">
        <PlanBadge plan={plan} />
      </div>

      <div className="flex-col-el flex-1 min-h-0 gap-1 p-3 overflow-hidden">
        {PLAN_COMPARISON_SECTIONS.map((section) => {
          if (section.kind === "single") {
            const { key, icon, labelKey } = section.item;
            const text = formatItemValue(limits, section.item);
            return (
              <div key={key} className="flex-row-el items-center gap-2 min-w-0 py-1">
                <span className="material-symbols-outlined type-body shrink-0 text-[color:var(--plan-accent)]">
                  {icon}
                </span>
                <p className="type-text type-body text-textcolor-black flex-1 truncate leading-tight !text-left">
                  {I18n.t(labelKey)}
                </p>
                <p className="type-text type-body leading-tight whitespace-nowrap text-[color:var(--plan-accent)]">
                  {text}
                </p>
              </div>
            );
          }
          const { group } = section;
          return (
            <div key={group.groupKey} className="flex-col-el gap-1 min-w-0 py-1">
              <div className="flex-row-el items-center gap-2 min-w-0">
                <span className="material-symbols-outlined type-body shrink-0 text-[color:var(--plan-accent)]">
                  {group.icon}
                </span>
                <p className="type-text type-body text-textcolor-black flex-1 truncate leading-tight !text-left">
                  {I18n.t(group.labelKey)}
                </p>
              </div>
              <div className="flex-col-el gap-1">
                {group.items.map((sub) => (
                  <div key={sub.key} className="flex-row-el items-center gap-2 min-w-0">
                    <span className="material-symbols-outlined type-body shrink-0 text-[color:var(--plan-accent)]">
                      subdirectory_arrow_right
                    </span>
                    <p className="type-text type-body text-textcolor-black/80 flex-1 truncate leading-tight !text-left">
                      {I18n.t(sub.labelKey)}
                    </p>
                    <p className="type-text type-body leading-tight whitespace-nowrap text-[color:var(--plan-accent)]">
                      {formatItemValue(limits, sub)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex-col-el items-center justify-center gap-2 p-3 shrink-0 bg-[color:var(--plan-accent)]/8">
        {isFreeOrTrial ? (
          <p className="type-text type-title leading-tight text-[color:var(--plan-accent)]">
            {I18n.t("MSG_USER_PLAN_PRICE_FREE")}
          </p>
        ) : (
          (priceString || !isCurrent) && (
            <div className="flex-col-el items-center gap-1 w-full">
              {showBillingToggle && (
                <UserPlanBillingToggle value={billingPeriod} onChange={onChangeBillingPeriod} />
              )}
              <div className="relative flex-row-el items-center justify-center w-full">
                {showAnnualDealBadge && (
                  <span className="absolute -top-3 right-4 z-10 flex-row-el items-center justify-center h-6 rounded-full bg-orange-100 px-2 shadow-[0_2px_0_rgba(211,93,26,0.24)] pointer-events-none">
                    <span className="type-text type-sm leading-tight whitespace-nowrap text-white">
                      {I18n.t("MSG_USER_PLAN_BILLING_ANNUAL_DEAL_BADGE")}
                    </span>
                  </span>
                )}
                <p className="type-text type-title leading-tight text-[color:var(--plan-accent)]">
                  {priceString ?? ""}
                </p>
              </div>
            </div>
          )
        )}

        {isCurrent ? (
          <div className="flex-row-el items-center justify-center gap-2 w-full rounded-xl py-2 px-4 border-2 border-solid bg-white border-[color:var(--plan-accent)]">
            <span className="material-symbols-outlined type-md shrink-0 text-[color:var(--plan-accent)]">
              check_circle
            </span>
            <span className="type-text type-body leading-tight whitespace-nowrap text-[color:var(--plan-accent)]">
              {I18n.t("MSG_USER_PLAN_CURRENT")}
            </span>
          </div>
        ) : (
          category &&
          onUpgrade && (
            <div className="flex-col-el gap-2 w-full">
              <button
                type="button"
                onClick={handleUpgradeClick}
                className="type-inherit-font flex-row-el items-center justify-center gap-2 w-full border-0 rounded-xl py-2 px-3 bg-[#2bdc98] text-white shadow-[0_3px_0_#167752,0_4px_10px_rgba(63,74,97,0.1)] active:translate-y-[2px] transition-transform cursor-pointer"
              >
                <span className="material-symbols-outlined type-md text-white shrink-0">
                  shopping_cart
                </span>
                <span className="type-text type-body text-white leading-tight whitespace-nowrap">
                  {I18n.t("MSG_USER_PLAN_BUY_BTN")}
                </span>
              </button>
              <AskParentAction
                variant="card"
                planCategory={category}
                billingPeriod={billingPeriod}
              />
            </div>
          )
        )}
      </div>
    </div>
  );
};
